import type { ProductType, Prompt } from "@1js/pl-card-components";
import { useMsal } from "@azure/msal-react";
import { AppId } from "@microsoft/teams-js";
import React from "react";
import { BIZCHAT_METAOS_APP_ID, BIZCHAT_METAOS_PAGE_ID } from "../../config";
import {
  type HandoffCallbackResult,
  type HandoffErrorsType,
} from "../../hooks/handoff.types";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useHandoffService } from "../../hooks/useHandoffService";
import { MetaOSAppContext } from "../../pages/metaos/MetaOSAppContext";
import {
  recentFilesByProductAsync,
  type HandoffFile,
  type RecentFiles,
} from "../../services/workingsetfiles-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import {
  getSubPageId,
  isTeamsApp,
  navigateToApp,
  type AppNavigationParameters,
} from "../../utils/url";
import { type HandoffFileSelectionMenuConfiguration } from "../HandoffFileSelectionMenu/HandoffFileSelectionMenu.web";
import type { LoaderStateType } from "./Loader/Loader.web.hook";

export type HandoffProps = {
  serviceHost: string;
  locale: string;
};

export type LoaderConfigurationType =
  | {
      prompt: Prompt;
      destination: ProductType | string;
      state: LoaderStateType;
      error: HandoffErrorsType;
      abortController?: AbortController;
    }
  | undefined;

export type HandoffResult = {
  recentFiles: RecentFiles;
  onHandoffFileSelectionMenuOpenChange: () => void;
  handoffFileSelectionMenuConfiguration: HandoffFileSelectionMenuConfiguration;
  onLoaderCancel: () => void;
  onLoaderCopy: () => void;
  handoffLoadingWindow: WindowProxy | null;
  loaderConfiguration:
    | {
        prompt: Prompt;
        destination: ProductType | string;
        state: LoaderStateType;
      }
    | undefined;
  onTryInAppBadgeAction: (
    prompt: Prompt,
    product: ProductType | string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  onTryByCopyBadgeAction: (
    prompt: Prompt,
    product: ProductType | string,
    tagRef: React.MutableRefObject<null>
  ) => void;
};

type ActiveHandoff = {
  serviceHost: string;
  locale: string;
  product: ProductType | string;
  prompt: Prompt;
  file: HandoffFile | undefined;
};

export const useHandoff = (props: HandoffProps): HandoffResult => {
  const { serviceHost, locale } = props;
  const telemetry = useTelemetry();
  const {
    isTryInRetrieveWorkingFiles,
    isUseAccessUrlEnabled,
    isUseNewHandoffLoadingWindowEnabled,
    isBizChatTryInTeamsAppEnabled,
  } = useFeatureEnablement();
  const [loaderConfiguration, setLoaderConfiguration] =
    React.useState<LoaderConfigurationType>(undefined);

  const [handoffLoadingWindow, setHandoffLoadingWindow] =
    React.useState<WindowProxy | null>(null);
  const [activeHandoff, setActiveHandoff] = React.useState<
    ActiveHandoff | undefined
  >(undefined);

  const onSuccess = React.useCallback(
    (handoffResult: HandoffCallbackResult, product: string) => {
      setLoaderConfiguration(undefined);
      if (
        isTeamsApp() &&
        (product === "BizChat" || product == "CWC") &&
        isBizChatTryInTeamsAppEnabled
      ) {
        // For Teams desktop app we need to use Teams SDK to load BizChat
        const navigateParams: AppNavigationParameters = {
          appId: new AppId(BIZCHAT_METAOS_APP_ID),
          pageId: BIZCHAT_METAOS_PAGE_ID,
          webUrl: new URL(handoffResult.handoffUrl),
          subPageId: getSubPageId(handoffResult.handoffUrl),
        };
        navigateToApp(navigateParams);
        return;
      }

      if (handoffLoadingWindow) {
        // window was already opened,
        // stop rendering to window
        setHandoffLoadingWindow(null);
        // only need to navigate
        handoffLoadingWindow.location.replace(handoffResult.handoffUrl);
      } else {
        // open new window after retrieving handoff url
        const handoffWindow = window.open(handoffResult.handoffUrl);
        if (!isTeamsApp() && !handoffWindow) {
          throw new Error("PopupBlocked");
        }
      }
    },
    [handoffLoadingWindow, isBizChatTryInTeamsAppEnabled]
  );

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const { handoff, isProductNeedsFile } = useHandoffService();

  const onError = React.useCallback(
    (prompt: Prompt, destination: string, error: HandoffErrorsType) => {
      telemetry.sendTryInAppEventError(prompt, destination, error);
      if (handoffLoadingWindow) {
        handoffLoadingWindow.close();
        setHandoffLoadingWindow(null);
      }
      setLoaderConfiguration((config: LoaderConfigurationType) => {
        return {
          ...config,
          prompt,
          destination,
          state: "error",
          error: error as HandoffErrorsType,
        } as LoaderConfigurationType;
      });
    },
    [
      telemetry,
      handoffLoadingWindow,
      setLoaderConfiguration,
      setHandoffLoadingWindow,
    ]
  );

  const openLoader = React.useCallback(
    (
      prompt: Prompt,
      product: ProductType | string,
      state: LoaderStateType,
      abortController?: AbortController
    ) => {
      telemetry.sendTryInAppEventLoading(prompt, product);
      setLoaderConfiguration((_) => {
        return {
          prompt,
          destination: product,
          state,
          error: "UnknownError",
          abortController,
        };
      });
    },
    [telemetry, setLoaderConfiguration]
  );

  const onTryInAppBadgeAction = React.useCallback(
    async (
      prompt: Prompt,
      product: ProductType | string,
      tagRef: React.MutableRefObject<null>
    ) => {
      const openHandoffLoadingWindow = () => {
        let windowRef: WindowProxy | null = null;
        if (isUseNewHandoffLoadingWindowEnabled) {
          windowRef = window.open("", "_blank");
          if (!isTeamsApp() && !windowRef) {
            onError(prompt, product, "PopupBlockedNewWindow");
          } else if (windowRef) {
            setHandoffLoadingWindow(windowRef);
          }
        }
      };

      if (!isProductNeedsFile(product, prompt)) {
        openHandoffLoadingWindow();
        setActiveHandoff({
          product,
          locale,
          serviceHost,
          prompt,
          file: undefined,
        });

        return;
      }
      setHandoffFileSelectionMenuConfiguration({
        isOpen: true,
        product: product,
        openRef: tagRef,
        onAction: async (file: HandoffFile | undefined) => {
          openHandoffLoadingWindow();
          setActiveHandoff({
            product,
            locale,
            serviceHost,
            prompt,
            file,
          });
        },
      });
    },
    [
      isUseNewHandoffLoadingWindowEnabled,
      setHandoffLoadingWindow,
      isProductNeedsFile,
      serviceHost,
      locale,
      onError,
    ]
  );

  const onTryByCopyBadgeAction = React.useCallback(
    (prompt: Prompt, product: ProductType | string) => {
      openLoader(prompt, product, "copy", undefined);
      return;
    },
    [openLoader]
  );

  const [
    handoffFileSelectionMenuConfiguration,
    setHandoffFileSelectionMenuConfiguration,
  ] = React.useState<HandoffFileSelectionMenuConfiguration>({
    product: "",
    isOpen: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onAction: () => {},
    openRef: React.useRef(null),
  });

  const onHandoffFileSelectionMenuOpenChange = React.useCallback(() => {
    setHandoffFileSelectionMenuConfiguration((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  }, []);

  const [recentFiles, setRecentFiles] = React.useState<RecentFiles>({
    Word: [],
    Excel: [],
    PowerPoint: [],
  });

  React.useEffect(() => {
    if (!isTryInRetrieveWorkingFiles) {
      return;
    }
    recentFilesByProductAsync(
      serviceHost,
      locale,
      isUseAccessUrlEnabled,
      instance
    ).then((files) => setRecentFiles((_) => files));
  }, [
    locale,
    serviceHost,
    isTryInRetrieveWorkingFiles,
    isUseAccessUrlEnabled,
    instance,
  ]);

  // This effect is responsible for handling the handoff process
  // This is run after the state is updated for the handoffLoadingWindow and setting parameters in
  // activeHandoff
  React.useEffect(() => {
    const asyncRunHandoff = async () => {
      if (activeHandoff) {
        const abortController = new AbortController();
        openLoader(
          activeHandoff.prompt,
          activeHandoff.product,
          "loading",
          abortController
        );
        try {
          const handoffResult = await handoff({
            host: activeHandoff.serviceHost,
            locale: activeHandoff.locale,
            prompt: activeHandoff.prompt,
            destination: activeHandoff.product,
            file: activeHandoff.file,
            abortController: abortController,
          });

          if (!abortController.signal.aborted) {
            telemetry.sendTryInAppEventSuccess(
              activeHandoff.prompt,
              activeHandoff.product,
              handoffResult.queryId
            );
            onSuccess(handoffResult, activeHandoff.product);
          }
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          onError(
            activeHandoff.prompt,
            activeHandoff.product,
            (error as Error).message as HandoffErrorsType
          );
        } finally {
          setActiveHandoff(undefined);
        }
      }
    };
    asyncRunHandoff();
    // This effect should only run when activeHandoff changes, no other deps here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHandoff]);

  const onLoaderCancel = React.useCallback(() => {
    if (!loaderConfiguration) {
      return;
    }

    if (handoffLoadingWindow) {
      handoffLoadingWindow.close();
      setHandoffLoadingWindow(null);
    }

    if (loaderConfiguration.abortController) {
      loaderConfiguration.abortController.abort();
    }

    telemetry.sendTryInAppEventCancelled(
      loaderConfiguration.prompt,
      loaderConfiguration.destination,
      "UserCancelled"
    );
    setLoaderConfiguration(undefined);
  }, [
    loaderConfiguration,
    handoffLoadingWindow,
    telemetry,
    setLoaderConfiguration,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onLoaderCopy = React.useCallback(() => {
    if (!loaderConfiguration) {
      return;
    }
    telemetry.sendTryInAppEventCopy(
      loaderConfiguration.prompt,
      loaderConfiguration.destination,
      loaderConfiguration?.error
    );
  }, [loaderConfiguration, telemetry]);

  return {
    recentFiles,
    onHandoffFileSelectionMenuOpenChange,
    handoffFileSelectionMenuConfiguration,
    onLoaderCancel,
    loaderConfiguration,
    onLoaderCopy,
    onTryInAppBadgeAction,
    onTryByCopyBadgeAction,
    handoffLoadingWindow,
  };
};
